import { AgGridChoiceFilterAdapter, AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { FactorAppliesToLinkValue, FactorStatusValue } from 'constantsProject';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type {
    ContractRatioRowData,
    PriceListRatioRowData,
    PriceListRatioRowDataFilterInput,
    RatioRecordStatusEnum,
} from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import type { AnyObject } from 'types/AnyObject';
import { Button, Checkbox, cnMixSpace } from 'uikit';
import { createWhereVariablesGetter } from 'utils/getWhereVariables';
import { getYesNoStatus } from 'utils/getYesNoTableStatus';

import { IconExpand } from '@consta/icons/IconExpand';
import type { ColDef, ColGroupDef } from 'ag-grid-community';
import debounce from 'debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
    getContractChangeRatioForIncludingCodeFilter,
    getContractChangeRatioForIncludingStatusFilter,
    getContractChangeRatioForIncludingWorkConditionsFilter,
    uerRatioRowForIncludingToContractChangeGql,
} from '../AgreementsQueries';

const TITLE = 'Добавление коэффициентов из Прейскуранта коэффициентов';
const SEARCH_PLACEHOLDER = 'Поиск коэффициентов в прейскуранте';

const getWhereVariables = createWhereVariablesGetter<PriceListRatioRowDataFilterInput | undefined>({
    searchStringFields: ['code', 'workConditions'],
    filtersToWhere: (selectedFilters: [string, boolean[] | string[]][]) =>
        selectedFilters.map(([fieldName, value]) => {
            if (
                fieldName === 'isIncludedInWorkOrder' ||
                fieldName === 'isIncludedInAct' ||
                fieldName === 'isIncludedInWorkOrderChange'
            ) {
                return {
                    [fieldName]: {
                        eq: value[0] === 'Да',
                    },
                };
            }

            return {
                [fieldName]: {
                    in: value,
                },
            };
        }),
});

export const useAgreementTableAddRatio = (openModal: boolean, disabledByStatus: boolean) => {
    const { agreementId: agreementIdString } = useParams();
    const { state }: { state: { selected: number } } | undefined = useLocation();

    const [isSubmittedDataTable, setIsSubmittedDataTable] = useState<boolean>(false);
    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
    const [searchString, setSearchString] = useState('');
    const [queryWhere, setQueryWhere] = useState<any>(undefined);

    const [ratioNameForInfo, setRatioNameForInfo] = useState<string | undefined>(undefined);

    const agreementId = Number(agreementIdString);

    const variables = useMemo(
        () => ({
            contractChangeId: agreementId,
            where: getWhereVariables(queryWhere),
        }),
        [agreementId, queryWhere],
    );

    const [ratioAgreementsData, ratioAgreementsStatus, ratioAgreementsErrors, ratioAgreementsRequest] = useRequest({
        requestId: 'getAgreementPriceListRatio',
        query: uerRatioRowForIncludingToContractChangeGql,
        variables,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'getAgreementPriceListRatio',
        requestFn: ratioAgreementsRequest,
        requestVariables: variables,
    });

    const handleChangeCheckbox = useCallback(
        (rowId: number) =>
            ({ checked }: { checked: boolean }) => {
                if (rowId) {
                    setCheckedIds((ids) => ({ ...ids, [rowId]: checked }));
                }
            },
        [],
    );

    const displayRatioInfoHandler = useCallback(
        (name: string) => () => {
            setRatioNameForInfo(name);
        },
        [],
    );

    const closeRatioInfoTable = useCallback(() => {
        setRatioNameForInfo(undefined);
    }, []);

    const handleTableFilterUpdated = useCallback((value: { checkedValues: string[]; fieldName: string }) => {
        setQueryWhere((prevFilters: any) => ({ ...prevFilters, [value.fieldName]: value.checkedValues }));
    }, []);

    const columns = useMemo<(ColDef<ContractRatioRowData> | ColGroupDef<ContractRatioRowData>)[]>(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                width: 80,
                resizable: false,
                cellClass: 'centeredAgGridCell',
                cellRenderer: ({ data }: { data: PriceListRatioRowData }) => {
                    if (!data) {
                        return '';
                    }

                    return (
                        <>
                            <Checkbox
                                checked={checkedIds[data.id]}
                                onChange={handleChangeCheckbox(data?.id)}
                                disabled={disabledByStatus}
                            />
                            <Button
                                onlyIcon
                                iconLeft={IconExpand}
                                view="clear"
                                size="xs"
                                iconSize="s"
                                className={cnMixSpace({
                                    mL: 's',
                                })}
                                onClick={displayRatioInfoHandler(data.code)}
                            />
                        </>
                    );
                },
            },
            {
                headerName: 'Шифр',
                field: 'code',
                width: 130,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId: agreementId },
                    changeFilterHandler: handleTableFilterUpdated,
                    dataAccessor: 'uerContractChangeRatioForIncludingCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getContractChangeRatioForIncludingCodeFilter,
                },
            },
            {
                field: 'workConditions',
                headerName: 'Наименование',
                minWidth: 50,
                flex: 1,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId: agreementId },
                    changeFilterHandler: handleTableFilterUpdated,
                    dataAccessor: 'uerContractChangeRatioForIncludingWorkConditionsFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getContractChangeRatioForIncludingWorkConditionsFilter,
                },
            },
            {
                field: 'kozp',
                headerName: 'К озп',
                width: 65,
            },
            {
                field: 'kem',
                headerName: 'К эмм',
                width: 65,
            },
            {
                field: 'knr',
                headerName: 'К нр',
                width: 65,
            },
            {
                field: 'ksp',
                headerName: 'К сп',
                width: 65,
            },
            {
                field: 'kmat',
                headerName: 'К мат',
                width: 65,
            },
            {
                field: 'ktz',
                headerName: 'К тз озп',
                width: 80,
            },
            {
                field: 'ktzzpm',
                headerName: 'К тз зпм',
                width: 80,
            },
            {
                field: 'status',
                headerName: 'Статус записи',
                valueFormatter: ({ data }) => (data ? FactorStatusValue[data.status] : ''),
                width: 132,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId: agreementId },
                    changeFilterHandler: handleTableFilterUpdated,
                    dataAccessor: 'uerContractChangeRatioForIncludingStatusFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Статус записи',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: RatioRecordStatusEnum }) =>
                                FactorAppliesToLinkValue[value],
                        },
                    ],
                    query: getContractChangeRatioForIncludingStatusFilter,
                },
            },
            {
                headerName: 'Используется',
                marryChildren: true,
                children: [
                    {
                        field: 'isIncludedInWorkOrder',
                        headerName: 'НЗ',
                        width: 85,
                        valueFormatter: ({ data }) => getYesNoStatus(data?.isIncludedInWorkOrder) ?? '',
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: handleTableFilterUpdated,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInAct',
                        headerName: 'Акте',
                        width: 95,
                        valueFormatter: ({ data }) => getYesNoStatus(data?.isIncludedInAct) ?? '',
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: handleTableFilterUpdated,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInWorkOrderChange',
                        headerName: 'Изм. к НЗ',
                        width: 95,
                        valueFormatter: ({ data }) => getYesNoStatus(data?.isIncludedInWorkOrderChange) ?? '',
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: handleTableFilterUpdated,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                ],
            },
        ],
        [
            agreementId,
            checkedIds,
            disabledByStatus,
            displayRatioInfoHandler,
            handleChangeCheckbox,
            handleTableFilterUpdated,
        ],
    );

    const handleCloseErrorAgreementTable = useCallback(() => setIsSubmittedDataTable(false), []);

    const handleSearch = useCallback(({ value }) => {
        const trimValue = value?.trim() || '';

        setQueryWhere((prevState: AnyObject) => ({
            ...prevState,
            searchString: trimValue,
        }));
    }, []);

    const debouncedSearch = useMemo(() => debounce(handleSearch, 500), [handleSearch]);

    const handleChange = useCallback(
        ({ value }) => {
            setSearchString(value);
            debouncedSearch({ value });
        },
        [debouncedSearch],
    );

    const handleClearSearch = useCallback(() => {
        if (searchString?.length) {
            setSearchString('');
            setQueryWhere((prevFilters: AnyObject) => ({
                ...prevFilters,
                searchString: undefined,
            }));
        }
    }, [searchString?.length]);

    useEffect(() => {
        if (state?.selected) {
            setCheckedIds((ids) => ({ ...ids, [state?.selected]: true }));
        }
    }, [handleChangeCheckbox, setCheckedIds, state?.selected]);

    useEffect(() => {
        setIsSubmittedDataTable(true);

        if (ratioAgreementsStatus === RequestStatus.SUCCESS && !ratioAgreementsErrors) {
            setIsSubmittedDataTable(false);
        }
    }, [ratioAgreementsErrors, ratioAgreementsStatus]);

    useEffect(() => {
        if (ratioAgreementsData?.uerRatioRowForIncludingToContractChange?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                ratioAgreementsData.uerRatioRowForIncludingToContractChange.items ?? [],
                ratioAgreementsData.uerRatioRowForIncludingToContractChange?.totalCount,
            );
        }
    }, [
        getRowParams,
        ratioAgreementsData?.uerRatioRowForIncludingToContractChange?.items,
        ratioAgreementsData?.uerRatioRowForIncludingToContractChange?.totalCount,
    ]);

    useEffect(() => {
        if (!openModal) {
            setQueryWhere({});
        }
    }, [openModal]);

    return {
        titleModal: TITLE,
        searchPlaceholder: SEARCH_PLACEHOLDER,
        agreementId,
        checkedIds,
        setCheckedIds,
        searchString,
        setSearchString,
        agreementsData: ratioAgreementsData?.uerRatioRowForIncludingToContractChange,
        agreementsStatus: ratioAgreementsStatus,
        agreementsErrors: ratioAgreementsErrors,
        finalColumns: columns,
        isSubmittedDataTable,
        handleCloseErrorAgreementTable,
        handleSearch,
        handleChange,
        handleClearSearch,
        closeRatioInfoTable,
        ratioNameForInfo,
        dataSource,
    };
};
